import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUser,
    faPlus,
    faCheck,
    faXmark,
    faPhone,
    faPalette,
    faToggleOn,
} from "@fortawesome/free-solid-svg-icons";

import PaginatorComponent from "../../general/auxiliar/ui/PaginatorComponent";
import CustomForm from "../../general/form/CustomForm";
import Collapse from "../../general/auxiliar/collapse/Collapse";
import EmptyTable from "../../general/auxiliar/table/EmptyTable";
import LoadingTable from "../../general/auxiliar/table/LoadingTable";
import OverviewTable from "../../general/auxiliar/table/OverviewTable";
import TableBadge from "../../general/auxiliar/table/TableBadge";
import useFormWithDisable from "../../../auxiliar/customHooks/useFormWithDisable";
import usePaginationFilterEffects from "../../../auxiliar/customHooks/usePaginationFilterEffects";
import { getAllPhysiotherapists } from "../../../actions/physiotherapist";
import { handleSubmit, getTableState } from "../../../auxiliar/auxFunctions";
import { OverviewCardList } from "../../general/auxiliar/table/OverviewCard";
import { colors } from "../../../auxiliar/colors";

const PhysioCardList = ({ physios, navigate }) => {
    const renderTitle = (physio) => `${physio.name} ${physio.surnames}`;

    const renderSubtitle = (physio) => (
        <div className="flex items-center gap-2 mt-1">
            <TableBadge variant={physio.active ? "success" : "error"} size="xs">
                <FontAwesomeIcon
                    icon={physio.active ? faCheck : faXmark}
                    className="mr-1 w-2 h-2 sm:w-3 sm:h-3"
                />
                {physio.active ? "Activo" : "Inactivo"}
            </TableBadge>
        </div>
    );

    const renderFooter = (physio) => (
        <div className="space-y-2 text-xs sm:text-sm">
            {physio.phone_number && (
                <div className="flex items-center gap-2 text-base-content/70">
                    <FontAwesomeIcon
                        icon={faPhone}
                        className="w-3 h-3 text-base-content/40 flex-shrink-0"
                    />
                    <span className="truncate">{physio.phone_number}</span>
                </div>
            )}
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faPalette}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">{colors[physio.color]}</span>
                <div
                    className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border border-base-300 flex-shrink-0"
                    style={{ backgroundColor: physio.color }}
                ></div>
            </div>
        </div>
    );

    const getDetailPath = (physioId) => `/services/physiotherapist/${physioId}`;

    return (
        <OverviewCardList
            data={physios}
            navigate={navigate}
            getDetailPath={getDetailPath}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
            renderFooter={renderFooter}
        />
    );
};

const PhysioTable = ({ physios, navigate }) => {
    const columns = [
        {
            label: "Nombre",
            icon: faUser,
            width: "w-[28%]",
        },
        {
            label: "Estado",
            icon: faToggleOn,
            width: "w-[18%]",
        },
        {
            label: "Teléfono",
            icon: faPhone,
            width: "w-[18%]",
        },
        {
            label: "Color",
            icon: faPalette,
            width: "w-[18%]",
        },
    ];

    const getRowData = (physio, id) => {
        const fullName = `${physio.name} ${physio.surnames}`;

        return [
            <div
                key={`${id}-name`}
                className="font-semibold text-base-content truncate"
                title={fullName}
            >
                {fullName}
            </div>,
            <TableBadge
                key={`${id}-status`}
                variant={physio.active ? "success" : "error"}
                size="sm"
            >
                <FontAwesomeIcon icon={physio.active ? faCheck : faXmark} className="mr-1" />
                {physio.active ? "Activo" : "Inactivo"}
            </TableBadge>,
            <div key={`${id}-phone`} className="text-base-content/70">
                {physio.phone_number || "-"}
            </div>,
            <div key={`${id}-color`} className="flex items-center gap-2">
                <div
                    className="w-6 h-6 rounded-full border-2 border-base-300 shadow-sm"
                    style={{ backgroundColor: physio.color }}
                ></div>
                <span className="text-base-content/70 truncate">{colors[physio.color]}</span>
            </div>,
        ];
    };

    const getDetailPath = (physioId) => `/services/physiotherapist/${physioId}`;

    return (
        <OverviewTable
            data={physios}
            navigate={navigate}
            columns={columns}
            getRowData={getRowData}
            getDetailPath={getDetailPath}
            actionsWidth="w-[18%]"
        />
    );
};

const OverviewPhysios = () => {
    const navigate = useNavigate();

    const { register, watch, errors } = useFormWithDisable();

    const { id: clinicId } = useSelector((state) => state.activeClinic);

    const [physios, setPhysios] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        clinic_id: clinicId,
        order: "name",
        per_page: 10,
        page: 1,
        only_active: true,
    });

    const writtenFilter = {
        name: watch("name") ?? "",
        surnames: watch("surnames") ?? "",
        only_active: watch("only_active") ?? true,
    };

    const disabled = usePaginationFilterEffects(
        filter,
        writtenFilter,
        getAllPhysiotherapists,
        setPhysios,
        setFilter,
    );

    const formConfig = {
        name: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Nombre",
            placeholder: "Nombre",
            name: "name",
            defaultValue: "",
        },
        surnames: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Apellidos",
            placeholder: "Apellidos",
            name: "surnames",
            defaultValue: "",
        },
        only_active: {
            condition: true,
            disabled: false,
            defaultValue: true,
            type: "checkbox",
            label: "Excluir inactivos",
            name: "only_active",
        },
    };

    const tableState = getTableState(physios);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando fisioterapeutas..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faUser}
                        title="No hay fisioterapeutas registrados"
                        description="Añade el primer fisioterapeuta para comenzar a gestionar tu equipo."
                        buttonText="Nuevo fisioterapeuta"
                        onButtonClick={handleSubmit(navigate, "/services/physiotherapist/register")}
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <PhysioCardList physios={physios} navigate={navigate} />
                        <PhysioTable physios={physios} navigate={navigate} />
                    </>
                );
        }
    };

    return (
        <section>
            <h2 className="sr-only">
                Personaliza los tipos de sesión de tu clínica de fisioterapia en MiFisio Agenda -
                Tipos de sesión personalizados
            </h2>

            <Collapse isMainTitle={true} title="Fisioterapeutas" faModality="filter">
                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            </Collapse>

            <div className="mt-6">
                <div className="my-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    {/* Action button */}
                    <button
                        className="btn btn-primary btn-sm w-full sm:w-auto"
                        onClick={handleSubmit(navigate, "/services/physiotherapist/register")}
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Nuevo fisioterapeuta
                    </button>

                    {/* Pagination */}
                    <div className="w-full sm:w-auto">
                        <PaginatorComponent
                            pagination={physios.pagination}
                            setFilter={setFilter}
                            filter={filter}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {renderContent()}

                {/* Pagination for small screens - duplicated to improve UX */}
                <div className="w-full my-5 sm:w-auto lg:hidden">
                    <PaginatorComponent
                        pagination={physios.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                        showTotalItems={false}
                    />
                </div>
            </div>
        </section>
    );
};

export default OverviewPhysios;
